// ./src/components/features/Users/UserEditDrawer/UserEditDrawer.tsx
//
// Edit drawer for User records.
//
// Design:
// - Self-loading in edit mode via onLoad prop or UsersContext.get
// - Shows skeleton while loading record data
// - No local validators — server-side validation via OpResult (glovebox pattern)
// - Dirty state tracking via form serialization
// - Context support: reads get/update/isUpdating/lastError/clearError
//   from UsersContext automatically when available; props override context.
//
// Architecture follows LogbookEditDrawer patterns:
// - Deferred open (requestAnimationFrame) so drawer transition isn't blocked
// - Stable refs for callbacks used in effects
// - Combined load + operation errors via effectiveError
// - getFormErrors() for field-level error display

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  TextInput,
  Text,
  Stack,
  SimpleGrid,
  Divider,
  Skeleton,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";

import { User, UserInput } from "@/utils/api/UserService";
import { getFormErrors, formatErrors } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";

import { useOptionalUsersContext } from "@/contexts/UsersContext";

import { AppDrawer } from "@/components/generic/AppDrawer";
import { StatusSelector } from "@/components/generic/StatusSelector";

// ============================================
// Types
// ============================================

export interface UserEditDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Function to close the drawer */
  onClose: () => void;

  /** User ID to load in edit mode (null/undefined for create mode) */
  userId?: string | null;

  /**
   * Optional override for loading a user by ID.
   * If not provided, uses UsersContext.get when available.
   */
  onLoad?: (id: string) => Promise<OpResult>;

  /**
   * Optional override for updating a user.
   * If not provided, uses UsersContext.update when available.
   */
  onUpdate?: (id: string, data: UserInput) => Promise<OpResult>;

  /**
   * Called after a user is successfully updated.
   * Receives the updated User so the page can react
   * (e.g. highlight the updated row).
   */
  onUpdated?: (user: User) => void;

  /** Whether update operation is in progress (prop-based override) */
  isUpdating?: boolean;

  /** Last error from operations (prop-based override) */
  lastError?: OpResult | null;

  /** Callback to clear errors (prop-based override) */
  onClearError?: () => void;
}

// ============================================
// Form Types
// ============================================

interface FormData {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  status: number | null;
}

const INITIAL_FORM_DATA: FormData = {
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phone: "",
  status: 100,
};

/**
 * Fields recognized for OpResult field-level error mapping.
 * ⓘ `email` stays listed: it is rendered (read-only) and the server's refusal of it shows there.
 * ⚠ No `password` (security review, 2026-10-16): ms_auth refuses setting another member's password — and their email —
 *   through `userUpdate`; the person resets it themselves ("Forgot password"), or changes it on their own Profile page.
 */
const KNOWN_FORM_FIELDS = [
  "firstName",
  "middleName",
  "lastName",
  "email",
  "phone",
  "status",
];

// ============================================
// Helpers
// ============================================

const serializeFormSnapshot = (data: FormData): string => {
  return JSON.stringify(data);
};

/**
 * Invoke both error-clearing callbacks, but avoid calling the same
 * function reference twice (happens when no prop overrides are provided
 * and both resolve to usersContext.clearError).
 */
const clearAllErrors = (
  onClearError: (() => void) | undefined,
  clearOperationError: (() => void) | undefined
) => {
  onClearError?.();
  if (clearOperationError && clearOperationError !== onClearError) {
    clearOperationError();
  }
};

// ============================================
// Loading Skeleton
// ============================================

const UserEditDrawerSkeleton: React.FC = () => {
  return (
    <Stack gap="md">
      {/* Name fields */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <Skeleton height={60} radius="sm" />
        <Skeleton height={60} radius="sm" />
      </SimpleGrid>

      {/* Email and phone */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <Skeleton height={60} radius="sm" />
        <Skeleton height={60} radius="sm" />
      </SimpleGrid>

      {/* Password fields */}
      <Divider />
      <Skeleton height={60} radius="sm" />
      <Skeleton height={60} radius="sm" />

      {/* Status */}
      <Divider />
      <Skeleton height={60} radius="sm" />
    </Stack>
  );
};

// ============================================
// Component
// ============================================

export const UserEditDrawer: React.FC<UserEditDrawerProps> = ({
  opened,
  onClose,
  userId,
  onLoad,
  onUpdate,
  onUpdated,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const { t } = useTranslation(["users", "common", "validation"]);

  // ============================================
  // Context vs Props resolution
  // ============================================

  const usersContext = useOptionalUsersContext();

  const loadFn = onLoad ?? usersContext?.get;
  const updateFn = onUpdate ?? usersContext?.update;
  const updating = isUpdating || (usersContext?.isUpdating ?? false);
  const operationError = lastError ?? usersContext?.lastError ?? null;
  const clearOperationError = onClearError ?? usersContext?.clearError;

  const isEditMode = Boolean(userId);
  const isSubmitting = updating;

  // ============================================
  // Stable refs for callbacks used in effects
  // ============================================

  const clearOperationErrorRef = useRef(clearOperationError);
  clearOperationErrorRef.current = clearOperationError;

  const onClearErrorRef = useRef(onClearError);
  onClearErrorRef.current = onClearError;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;

  const tRef = useRef(t);
  tRef.current = t;

  // ============================================
  // Self-loading state
  // ============================================

  const [record, setRecord] = useState<User | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  // Deferred open flag — becomes true one frame after `opened` turns true.
  // This lets the drawer's open transition start before we fire the API call,
  // preventing the fetch from blocking/delaying the drawer appearance.
  const [isReadyToLoad, setIsReadyToLoad] = useState(false);

  useEffect(() => {
    if (opened) {
      const frameId = requestAnimationFrame(() => {
        setIsReadyToLoad(true);
      });
      return () => cancelAnimationFrame(frameId);
    } else {
      setIsReadyToLoad(false);
    }

    return;
  }, [opened]);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  // ============================================
  // Autofocus the first field
  // ============================================
  // Focus once the form is actually on screen. A short delay lets the drawer's
  // open animation + Mantine's focus trap settle first, otherwise our focus is
  // overridden by the trap's initial focus.
  const firstNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!opened || showLoadingSkeleton) return undefined;

    const id = setTimeout(() => {
      const el = firstNameInputRef.current;
      if (el) {
        el.focus();
        // Put the caret at the end so typing continues the existing value.
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    }, 120);

    return () => clearTimeout(id);
  }, [opened, showLoadingSkeleton]);

  // ============================================
  // Load record when opened in edit mode
  // ============================================

  useEffect(() => {
    let isCancelled = false;

    if (isReadyToLoad && isEditMode && userId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      const loadRecord = async () => {
        const result = await loadFnRef.current!(userId);

        if (isCancelled) return;

        if (result.didSucceed()) {
          const loaded = result.getDataFirst() as User;
          setRecord(loaded);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(
            result
          );
        }

        setIsLoadingRecord(false);
      };

      loadRecord();
    } else if (!opened) {
      // Reset when drawer closes
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [isReadyToLoad, opened, isEditMode, userId]);

  // ============================================
  // Form State
  // ============================================

  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);

  // ============================================
  // Dirty State Tracking
  // ============================================

  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }
    const current = serializeFormSnapshot(formData);
    setIsDirty(current !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  // ============================================
  // Error Handling (OpResult)
  // ============================================

  // Combine load errors with CRUD errors
  // The submit's OWN result — with an `onUpdate` override the call bypasses the
  // context, so context.lastError never fills in and errors vanish silently.
  const [submitError, setSubmitError] = useState<OpResult | null>(null);

  const effectiveError = loadError || submitError || operationError;

  const { fields: apiFieldErrors, genericErrors } = useMemo(() => {
    return getFormErrors(effectiveError, KNOWN_FORM_FIELDS);
  }, [effectiveError]);

  const formattedGenericError = useMemo(() => {
    return formatErrors(genericErrors);
  }, [genericErrors]);

  // ============================================
  // Form Reset on Open
  // ============================================

  useEffect(() => {
    if (!opened) return;

    // In edit mode, wait for record to be loaded before populating form
    if (isEditMode && !record) return;

    let newFormData: FormData;

    if (record) {
      // ── Edit mode ──
      newFormData = {
        firstName: record.firstName || "",
        middleName: record.middleName || "",
        lastName: record.lastName || "",
        email: record.email || "",
        phone: (record as any).phone || "",
        status: record.status != null ? record.status : 100,
      };
    } else {
      // ── Create mode (future-proofing) ──
      newFormData = { ...INITIAL_FORM_DATA };
    }

    setFormData(newFormData);
    initialSnapshotRef.current = serializeFormSnapshot(newFormData);
    setIsDirty(false);
    clearAllErrors(onClearErrorRef.current, clearOperationErrorRef.current);
    setSubmitError(null);
  }, [opened, record, isEditMode]);

  // ============================================
  // Handlers
  // ============================================

  const handleFieldChange = useCallback(
    (field: keyof FormData, value: string | number | null) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleTextInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      if (name) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    },
    []
  );

  // ============================================
  // Submit
  // ============================================

  const handleSubmit = useCallback(async () => {
    clearAllErrors(onClearError, clearOperationError);
    setLoadError(null);

    if (!isEditMode || !userId || !updateFn) return;

    const submitData: UserInput = {
      firstName: formData.firstName.trim(),
      middleName: formData.middleName.trim() || null,
      lastName: formData.lastName.trim(),
      // ⚠ No `email`: it is the person's own to change (ms_auth refuses it from anyone else, even unchanged).
      status: formData.status,
    };

    // Add phone if available
    if (formData.phone.trim()) {
      (submitData as any).phone = formData.phone.trim();
    }

    const result = await updateFn(userId, submitData);

    if (result && !result.didSucceed()) {
      setSubmitError(result);

      return;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);

      const savedUser = result.data ? (result.data as User[])[0] : null;
      if (savedUser) {
        onUpdated?.(savedUser);
      }

      onClose();
    }
  }, [
    formData,
    isEditMode,
    userId,
    updateFn,
    onClose,
    onClearError,
    clearOperationError,
    onUpdated,
  ]);

  // ============================================
  // Keyboard shortcuts
  // ============================================
  // ⌘/Ctrl+S or ⌘/Ctrl+Enter to save (Esc is AppDrawer's own — it asks before discarding). Works while a field is
  // focused (the shortcut hook lets Cmd/Ctrl combos through in inputs).
  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  // ============================================
  // Drawer title
  // ============================================

  const drawerTitle = useMemo(() => {
    if (isEditMode && record) {
      const name = [record.firstName, record.lastName].filter(Boolean).join(" ");
      return t("users:form.editTitle", {
        name,
        defaultValue: "Edit User{{name}}",
      }).replace("{{name}}", name ? ` — ${name}` : "");
    }
    return t("users:form.editTitleGeneric", { defaultValue: "Edit User" });
  }, [isEditMode, record, t]);

  // ============================================
  // Render
  // ============================================

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      responsive
      mobileHeightPercent={92}
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(apiFieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <UserEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          {/* Name Fields */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput
              ref={firstNameInputRef}
              name="firstName"
              label={t("users:form.fields.firstName.label", { defaultValue: "First Name" })}
              placeholder={t("users:form.fields.firstName.placeholder", { defaultValue: "Enter first name" })}
              value={formData.firstName}
              onChange={handleTextInputChange}
              error={apiFieldErrors.firstName}
              required
              withAsterisk
              size="md"
            />
            <TextInput
              name="lastName"
              label={t("users:form.fields.lastName.label", { defaultValue: "Last Name" })}
              placeholder={t("users:form.fields.lastName.placeholder", { defaultValue: "Enter last name" })}
              value={formData.lastName}
              onChange={handleTextInputChange}
              error={apiFieldErrors.lastName}
              required
              withAsterisk
              size="md"
            />
          </SimpleGrid>

          <TextInput
            name="middleName"
            label={t("users:form.fields.middleName.label", { defaultValue: "Middle Name" })}
            placeholder={t("users:form.fields.middleName.placeholder", { defaultValue: "Enter middle name" })}
            value={formData.middleName}
            onChange={handleTextInputChange}
            error={apiFieldErrors.middleName}
            size="md"
          />

          {/* Email and Phone */}
          <Divider />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput
              name="email"
              label={t("users:form.fields.email.label", { defaultValue: "Email" })}
              placeholder={t("users:form.fields.email.placeholder", { defaultValue: "Enter email address" })}
              value={formData.email}
              error={apiFieldErrors.email}
              type="email"
              readOnly
              description={t("users:form.fields.email.ownOnly", {
                defaultValue: "Only the person can change their email address",
              })}
              size="md"
            />
            <TextInput
              name="phone"
              label={t("users:form.fields.phone.label", { defaultValue: "Phone" })}
              placeholder={t("users:form.fields.phone.placeholder", { defaultValue: "Enter phone number" })}
              value={formData.phone}
              onChange={handleTextInputChange}
              error={apiFieldErrors.phone}
              type="tel"
              size="md"
            />
          </SimpleGrid>

          {/* ⓘ No password fields: a member changes their own password (Profile), or resets it from the sign-in page. */}
          <Text size="md" c="dimmed">
            {t("users:form.passwordOwnOnly", {
              defaultValue: "Passwords are personal: the person changes theirs on their Profile page, or uses “Forgot password” on the sign-in page.",
            })}
          </Text>

          {/* Status */}
          <Divider />
          <StatusSelector
            label={t("users:form.fields.status.label", { defaultValue: "Status" })}
            value={formData.status}
            onChange={(value) => handleFieldChange("status", value)}
            error={apiFieldErrors.status}
            size="md"
          />
        </Stack>
      )}
    </AppDrawer>
  );
};